import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { Loader2, PenLine } from 'lucide-react';
import {
  captureVisitEvvSignatureVisitsVisitIdEvvSignaturePost,
  getVisitEvvCaptureContextVisitsVisitIdEvvCaptureContextGet,
} from '../../services/api/sdk.gen';
import type {
  VisitEvvCaptureContext,
  VisitEvvLocationCapture,
} from '../../services/api/types.gen';
import {
  formatDateForDisplay,
  formatDateTimeInZone,
} from '../../utils/dateUtils';

type CaptureStep = 'confirm' | 'done';

class LocationCaptureError extends Error {
  constructor() {
    super('Location is needed when recording your Patient Signature.');
    this.name = 'LocationCaptureError';
  }
}

interface HotboxEvvCaptureDialogProps {
  open: boolean;
  visitId: string | null;
  onOpenChange: (open: boolean) => void;
  onCompleted: () => void;
}

function stepForState(
  state: VisitEvvCaptureContext['evv_capture_state'] | undefined
): CaptureStep {
  // Existing EVV evidence is review-only; the portal captures a fresh patient
  // signature in one step and must not add evidence to legacy clock records.
  if (state === 'not_started') return 'confirm';
  return 'done';
}

function responseData<T>(response: { data?: T; error?: unknown }): T {
  if (
    ('error' in response && response.error != null) ||
    response.data == null
  ) {
    throw response;
  }
  return response.data;
}

function clinicianDisplayName(context: VisitEvvCaptureContext): string | null {
  return context.clinician_name || context.clinician_email || null;
}

async function captureLocation(): Promise<VisitEvvLocationCapture> {
  if (!navigator.geolocation) throw new LocationCaptureError();
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      position =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
      () => reject(new LocationCaptureError()),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });
}

const SignatureCanvas: React.FC<{
  disabled: boolean;
  onChange: (signed: boolean) => void;
  canvasRef: React.RefObject<HTMLCanvasElement>;
}> = ({ disabled, onChange, canvasRef }) => {
  const drawingRef = useRef(false);

  const point = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = event.currentTarget;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * canvas.width,
      y: ((event.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const start = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    const canvas = event.currentTarget;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.setPointerCapture(event.pointerId);
    const p = point(event);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    drawingRef.current = true;
  };

  const move = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled || !drawingRef.current) return;
    const ctx = event.currentTarget.getContext('2d');
    if (!ctx) return;
    const p = point(event);
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#111827';
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    onChange(true);
  };

  const stop = (event: React.PointerEvent<HTMLCanvasElement>) => {
    drawingRef.current = false;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <canvas
      ref={canvasRef}
      width={720}
      height={220}
      aria-label='Patient Signature pad'
      className={`h-40 w-full touch-none rounded-md border bg-white ${
        disabled ? 'opacity-50' : ''
      }`}
      onPointerDown={start}
      onPointerMove={move}
      onPointerUp={stop}
      onPointerCancel={stop}
    />
  );
};

async function canvasToPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) resolve(blob);
      else reject(new Error('signature canvas export failed'));
    }, 'image/png');
  });
}

export const HotboxEvvCaptureDialog: React.FC<HotboxEvvCaptureDialogProps> = ({
  open,
  visitId,
  onOpenChange,
  onCompleted,
}) => {
  const [context, setContext] = useState<VisitEvvCaptureContext | null>(null);
  const [step, setStep] = useState<CaptureStep>('confirm');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [patientUnableToSign, setPatientUnableToSign] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [activeVisitId, setActiveVisitId] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const submittingRef = useRef(false);
  const signatureCaptureIdRef = useRef<string | null>(null);
  const signatureVisitIdRef = useRef<string | null>(null);

  useEffect(() => {
    submittingRef.current = submitting;
  }, [submitting]);

  useEffect(() => {
    if (!open) {
      if (submittingRef.current) return;
      signatureCaptureIdRef.current = null;
      signatureVisitIdRef.current = null;
      setActiveVisitId(null);
      return;
    }
    if (!visitId) return;
    const requestedVisitId = visitId;
    setActiveVisitId(requestedVisitId);
    if (signatureVisitIdRef.current !== requestedVisitId) {
      signatureCaptureIdRef.current = null;
      signatureVisitIdRef.current = requestedVisitId;
    }
    let cancelled = false;
    // Clear the previous visit before loading so a failed request cannot leave
    // its patient details and confirmation controls usable for this visit.
    setContext(null);
    setStep('confirm');
    setLoading(true);
    setError(null);
    setPatientUnableToSign(false);
    setHasSignature(false);
    getVisitEvvCaptureContextVisitsVisitIdEvvCaptureContextGet({
      path: { visit_id: requestedVisitId },
    })
      .then(response => {
        if (cancelled) return;
        const data = responseData(response);
        setContext(data);
        setStep(stepForState(data.evv_capture_state));
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Failed to load EVV capture context', error);
        setError('Unable to load Patient Signature. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, visitId]);

  const clearSignature = () => {
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx && canvasRef.current) {
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    setHasSignature(false);
  };

  const submitSignature = async () => {
    if (!activeVisitId) return;
    const submissionVisitId = activeVisitId;
    const submissionPatientUnableToSign = patientUnableToSign;
    if (!submissionPatientUnableToSign && !hasSignature) {
      setError('Ask the patient to sign or select Patient unable to sign.');
      return;
    }
    let signatureCanvas: HTMLCanvasElement | null = null;
    if (!submissionPatientUnableToSign) {
      signatureCanvas = canvasRef.current;
      if (!signatureCanvas) {
        setError('Ask the patient to sign or select Patient unable to sign.');
        return;
      }
    }
    // Keep this ID across retries so a lost response cannot create a second
    // patient signature for the same visit, but snapshot it before the async
    // location wait so another visit cannot inherit a failed submit's ID.
    if (!signatureCaptureIdRef.current) {
      signatureCaptureIdRef.current = crypto.randomUUID();
    }
    const submissionCaptureId = signatureCaptureIdRef.current;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      let signatureFile: Blob | null = null;
      if (signatureCanvas) {
        signatureFile = await canvasToPng(signatureCanvas);
      }
      const location = await captureLocation();
      const body = {
        capture_id: submissionCaptureId,
        patient_unable_to_sign: submissionPatientUnableToSign,
        latitude: location.latitude,
        longitude: location.longitude,
        address: location.address,
        signature_file: signatureFile,
      };
      const response =
        await captureVisitEvvSignatureVisitsVisitIdEvvSignaturePost({
          path: { visit_id: submissionVisitId },
          body,
        });
      responseData(response);
      signatureCaptureIdRef.current = null;
      onCompleted();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to submit Patient Signature', error);
      setError(
        error instanceof LocationCaptureError
          ? error.message
          : 'Unable to submit Patient Signature. Please try again.'
      );
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (submitting && !nextOpen) return;
    onOpenChange(nextOpen);
  };

  const displayedOpen = open || submitting;
  const title =
    step === 'done' ? 'Patient Signature complete' : 'Patient Signature';
  const submittingIcon = submitting ? (
    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
  ) : null;

  return (
    <Dialog open={displayedOpen} onOpenChange={handleOpenChange}>
      <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-[640px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <PenLine className='h-5 w-5 text-primary' />
            {title}
          </DialogTitle>
          <DialogDescription>
            Capture the patient&apos;s signature from the RogerEMR web portal.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className='flex items-center justify-center py-8 text-sm text-muted-foreground'>
            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            Loading...
          </div>
        ) : error && !context ? (
          <div className='py-6 text-sm text-destructive'>{error}</div>
        ) : context ? (
          <div className='max-h-[70vh] space-y-4 overflow-y-auto pr-1'>
            {step === 'confirm' && (
              <div className='space-y-4'>
                <div className='rounded-md border p-3 text-sm'>
                  <div className='mb-2 font-medium'>
                    Confirm patient and visit
                  </div>
                  <dl className='grid grid-cols-1 gap-x-3 gap-y-1 sm:grid-cols-[8rem_1fr]'>
                    <dt className='text-muted-foreground'>Patient</dt>
                    <dd>{context.patient_name}</dd>
                    <dt className='text-muted-foreground'>MRN</dt>
                    <dd>{context.patient_mrn ?? '—'}</dd>
                    <dt className='text-muted-foreground'>Date of birth</dt>
                    <dd>
                      {context.patient_date_of_birth
                        ? formatDateForDisplay(context.patient_date_of_birth)
                        : '—'}
                    </dd>
                    <dt className='text-muted-foreground'>
                      Start of admission
                    </dt>
                    <dd>
                      {context.start_of_admission
                        ? formatDateForDisplay(context.start_of_admission)
                        : '—'}
                    </dd>
                    <dt className='text-muted-foreground'>Visit</dt>
                    <dd>{context.visit_type}</dd>
                    <dt className='text-muted-foreground'>Visit date</dt>
                    <dd>{formatDateForDisplay(context.visit_date) || '—'}</dd>
                    <dt className='text-muted-foreground'>Current Date/Time</dt>
                    <dd>
                      {formatDateTimeInZone(
                        context.current_datetime,
                        context.agency_timezone
                      ) || '—'}
                    </dd>
                    {context.task_target_date && (
                      <>
                        <dt className='text-muted-foreground'>Target date</dt>
                        <dd>
                          {formatDateForDisplay(context.task_target_date)}
                        </dd>
                      </>
                    )}
                    <dt className='text-muted-foreground'>Clinician</dt>
                    <dd>{clinicianDisplayName(context) ?? '—'}</dd>
                    <dt className='text-muted-foreground'>Address</dt>
                    <dd>{context.patient_address ?? '—'}</dd>
                  </dl>
                </div>

                <div className='space-y-2'>
                  <div className='font-medium'>Patient Signature</div>
                  <p className='text-base'>
                    Please sign below to verify that{' '}
                    {clinicianDisplayName(context) ?? 'the clinician'} was in
                    your home on {formatDateForDisplay(context.visit_date)}.
                    Location is needed to verify your visit completion.
                  </p>
                  <SignatureCanvas
                    disabled={patientUnableToSign || submitting}
                    onChange={setHasSignature}
                    canvasRef={canvasRef}
                  />
                  <div className='flex flex-wrap items-center justify-between gap-3'>
                    <label className='flex items-center gap-2 text-sm'>
                      <Checkbox
                        checked={patientUnableToSign}
                        disabled={submitting}
                        onCheckedChange={checked => {
                          if (submitting) return;
                          setPatientUnableToSign(Boolean(checked));
                          if (checked) clearSignature();
                        }}
                      />
                      Patient unable to sign
                    </label>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={clearSignature}
                      disabled={submitting}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {step === 'done' && (
              <div className='py-4 text-sm text-muted-foreground'>
                Patient Signature evidence already exists for this visit. Open
                the Patient Signature view to review it.
              </div>
            )}

            {error && <div className='text-sm text-destructive'>{error}</div>}
          </div>
        ) : null}

        <DialogFooter className='gap-2 sm:gap-0'>
          <Button
            variant='outline'
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
          >
            Close
          </Button>
          {context && step === 'confirm' && (
            <Button onClick={submitSignature} disabled={submitting}>
              {submittingIcon}
              Submit Patient Signature
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
